import type { ApprovedSourceLanding, MarketingReview } from "./marketing-source-types.mjs";
export type { ApprovedSourceLanding } from "./marketing-source-types.mjs";
import { resolveSourceSnapshot } from "./source-snapshot-brands";
import { withUsCityEditorialDepth } from "./us-city-editorial-depth.mjs";

export interface LandingReviewRecord {
  id: number | string;
  reviewerName: string;
  rating: number;
  text: string;
  carId?: number;
  carName?: string | null;
  carImageUrl?: string | null;
  countryCode?: string | null;
  make?: string | null;
  model?: string | null;
  city?: string | null;
  state?: string | null;
}

// Both snapshots are prepared separately; glob keeps the build valid until they exist.
const sourceFiles = import.meta.glob("../content/approved-driveshare-sources.json", { eager: true, import: "default" }) as Record<string, Record<string, ApprovedSourceLanding>>;
const reviewFiles = import.meta.glob("../content/landingReviewHighlights.json", { eager: true, import: "default" }) as Record<string, { reviews?: LandingReviewRecord[] }>;

const sources = resolveSourceSnapshot(Object.values(sourceFiles)[0] ?? {});
const reviewRecords = resolveSourceSnapshot(Object.values(reviewFiles)[0]?.reviews ?? []);

const norm = (p: string) => `/${String(p || "").replace(/^\/+|\/+$/g, "")}`;

export function getApprovedSourceLanding(path: string): ApprovedSourceLanding | null {
  const key = norm(path);
  const hit = Object.prototype.hasOwnProperty.call(sources, key) ? sources[key] : undefined;
  return hit && hit.heading && hit.introBlocks?.length ? withUsCityEditorialDepth(hit, key) : null;
}

/** Only verified same-country reviews; model/city matches rank first. Never a worldwide fallback. */
export function selectMarketReviews(
  scope: { countryCode?: string; city?: string; make?: string; model?: string },
  limit = 6,
): MarketingReview[] {
  if (!scope.countryCode) return [];
  const lc = (v?: string | null) => (v || "").toLowerCase();
  const rank = (r: LandingReviewRecord) =>
    (scope.model && lc(r.model) === lc(scope.model) ? 4 : 0) +
    (scope.make && lc(r.make) === lc(scope.make) ? 2 : 0) +
    (scope.city && lc(r.city) === lc(scope.city) ? 2 : 0);
  return reviewRecords
    .filter((r) => r.countryCode === scope.countryCode && r.text?.trim() && r.rating >= 4)
    .sort((a, b) => rank(b) - rank(a))
    .slice(0, limit)
    .map((r) => ({ id: r.id, reviewerName: r.reviewerName, rating: r.rating, text: r.text, carName: r.carName, carImageUrl: r.carImageUrl }));
}
